import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import NotificationList from "../../components/notification/notificationList";
import { useNotifications } from "../../features/notifications/notificationsContext";
import { ProfileId } from "../../types/types";

const FILTER_OPTIONS: { labelKey: string; defaultLabel: string; days: number }[] = [
  { labelKey: "filter_7_days", defaultLabel: "7 day", days: 7 },
  { labelKey: "filter_30_days", defaultLabel: "30 day", days: 30 },
  { labelKey: "filter_90_days", defaultLabel: "90 day", days: 90 },
];

const NotificationsPage: React.FC = () => {
  const { t } = useTranslation("notifications");
  const navigate = useNavigate();
  const {
    notifications,
    pendingIds,
    refreshNotifications,
    toggleReadStatus,
  } = useNotifications();
  const [selectedDays, setSelectedDays] = useState<number>(7);

  useEffect(() => {
    refreshNotifications(selectedDays);
  }, [selectedDays, refreshNotifications]);

  const handleProfileClick = (profileId: ProfileId): void => {
    navigate(`/profile?profileId=${profileId}`);
  };

  const renderFilterPills = () => (
    <div
      role="group"
      aria-label="Filter notifications by duration"
      className="inline-flex items-center gap-0.5 rounded-full bg-[#EBEBEC] p-1"
    >
      {FILTER_OPTIONS.map(({ labelKey, defaultLabel, days }) => {
        const isSelected = selectedDays === days;
        return (
          <button
            key={days}
            type="button"
            onClick={() => setSelectedDays(days)}
            className={`rounded-full px-3 py-1 text-xs sm:text-sm font-medium transition-all ${
              isSelected
                ? "bg-white text-gray-900 shadow-sm font-semibold"
                : "text-gray-500 hover:text-gray-900"
            }`}
          >
            {t(labelKey, defaultLabel)}
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="flex min-h-full flex-col font-sans">
      {/* Desktop page header */}
      <div className="hidden lg:flex items-center justify-between bg-white border-b border-slate-200/80 px-6 py-4 shrink-0">
        <h1 className="text-xl font-bold text-gray-900 font-['Plus_Jakarta_Sans']">
          {t("notifications_title", "Notifications")}
        </h1>
        {renderFilterPills()}
      </div>

      <div className="mx-auto flex w-full max-w-3xl flex-1 flex-col gap-4 px-4 py-6 sm:px-6">
        {/* Mobile filter pills */}
        <div className="flex items-center justify-end lg:hidden">
          {renderFilterPills()}
        </div>

        <NotificationList
          notifications={notifications}
          pendingIds={pendingIds}
          onToggleRead={toggleReadStatus}
          onProfileClick={handleProfileClick}
        />
      </div>
    </div>
  );
};

export default NotificationsPage;
